// Supermove
import {gql} from '@supermove/graphql';
import {BillItem} from '@supermove/models';
import {Currency, Percent, withFragment, Datetime} from '@supermove/utils';

// App
import Expression from '@shared/ast/Expression';
import FormulaPrettyPrinter from '@shared/formulas/src/FormulaPrettyPrinter';
import BillItemTypeCategory from '@shared/modules/Billing/enums/BillItemTypeCategory';
import BillItemTypeKind from '@shared/modules/Billing/enums/BillItemTypeKind';
import BillItemUnit from '@shared/modules/Billing/enums/BillItemUnit';
import BillStage from '@shared/modules/Billing/enums/BillStage';
import LineItemCategory from '@shared/modules/Billing/enums/LineItemCategory';
import LineItemKind from '@shared/modules/Billing/enums/LineItemKind';

const getDisplayBillStage = withFragment(
  (billItemType) => {
    if ((billItemType as any).billStage === BillStage.POST_SUBTOTAL) {
      return 'After Subtotal';
    }
    return 'Before Subtotal';
  },
  gql`
    fragment BillItemType_getDisplayBillStage on BillItemType {
      id
      billStage
    }
  `,
);

const getDisplayValue = withFragment(
  (billItemType) => {
    if ((billItemType as any).category === BillItemTypeCategory.SUPPLIES) {
      return (billItemType as any).amount === null
        ? 'TBD'
        : Currency.display((billItemType as any).amount);
    }
    if ((billItemType as any).kind === BillItemTypeKind.AMOUNT) {
      if ((billItemType as any).amount === null) {
        return 'TBD';
      }
      const amount = Currency.display((billItemType as any).amount);
      if ((billItemType as any).unit === BillItemUnit.HOUR) {
        return `${amount} / hour`;
      }
      return amount;
    }
    return (billItemType as any).percentage === null
      ? 'TBD'
      : Percent.display((billItemType as any).percentage);
  },
  gql`
    fragment BillItemType_getDisplayValue on BillItemType {
      id
      amount
      percentage
      category
      kind
      unit
    }
  `,
);

const getDisplayQuantity = withFragment(
  (billItemType, {isEnabledTbdBillItems}) => {
    if ((billItemType as any).billStage === BillStage.POST_SUBTOTAL && isEnabledTbdBillItems) {
      return 'TBD';
    }
    return BillItem.getEstimateQuantity(
      {
        minQuantity: (billItemType as any).minQuantity,
        maxQuantity: (billItemType as any).maxQuantity,
        unit: (billItemType as any).unit,
        kind: (billItemType as any).kind,
      },
      {isEnabledTbdBillItems},
    );
  },
  gql`
    fragment BillItemType_getDisplayQuantity on BillItemType {
      id
      minQuantity
      maxQuantity
      unit
      kind
      billStage
    }
  `,
);

const getDisplayName = withFragment(
  (billItemType) => {
    const moverPositionName = (billItemType as any).moverPosition
      ? ` (${(billItemType as any).moverPosition.name})`
      : '';
    return `${(billItemType as any).name}${moverPositionName}`;
  },
  gql`
    fragment BillItemType_getDisplayName on BillItemType {
      id
      name
      moverPosition {
        id
        name
      }
    }
  `,
);

const getIsSynced = withFragment(
  (billItemType) => {
    return !!(billItemType as any).identifier && !(billItemType as any).createdById;
  },
  gql`
    fragment BillItemType_getIsSynced on BillItemType {
      id
      identifier
      createdById
    }
  `,
);

const hasAmountFormula = withFragment(
  (billItemType) => {
    return !!(billItemType as any).amountFormulaId;
  },
  gql`
    fragment BillItemType_hasAmountFormula on BillItemType {
      id
      amountFormulaId
    }
  `,
);

const hasNameFormula = withFragment(
  (billItemType) => {
    return !!(billItemType as any).nameFormulaId;
  },
  gql`
    fragment BillItemType_hasNameFormula on BillItemType {
      id
      nameFormulaId
    }
  `,
);

const hasQuantityFormula = withFragment(
  (billItemType) => {
    return (
      !!(billItemType as any).minQuantityFormulaId || !!(billItemType as any).maxQuantityFormulaId
    );
  },
  gql`
    fragment BillItemType_hasQuantityFormula on BillItemType {
      id
      minQuantityFormulaId
      maxQuantityFormulaId
    }
  `,
);

const renderNameFormulaString = withFragment(
  (billItemType) => {
    if ((billItemType as any).nameFormula && (billItemType as any).nameFormula.astJson) {
      return new FormulaPrettyPrinter().print(
        Expression.fromJSON(JSON.parse((billItemType as any).nameFormula.astJson)),
      );
    }
  },
  gql`
    fragment BillItemType_renderNameFormulaString on BillItemType {
      id
      nameFormula {
        id
        astJson
      }
    }
  `,
);

const renderAmountFormulaString = withFragment(
  (billItemType) => {
    if ((billItemType as any).amountFormula && (billItemType as any).amountFormula.astJson) {
      return new FormulaPrettyPrinter().print(
        Expression.fromJSON(JSON.parse((billItemType as any).amountFormula.astJson)),
      );
    }
  },
  gql`
    fragment BillItemType_renderAmountFormulaString on BillItemType {
      id
      amountFormula {
        id
        astJson
      }
    }
  `,
);

const getLineItemCategoryFromBillItemTypeCategory = (billItemTypeCategory: any) => {
  const translationMap = {
    [BillItemTypeCategory.FEES]: LineItemCategory.EXTRA_CHARGE,
    [BillItemTypeCategory.DISCOUNTS]: LineItemCategory.EXTRA_DISCOUNT,
    [BillItemTypeCategory.SUPPLIES]: LineItemCategory.SUPPLIES,
  };
  return translationMap[billItemTypeCategory];
};

const getLineItemKindFromBillItemType = ({category, unit}: any) => {
  const translationMap = {
    [BillItemTypeCategory.FEES]:
      unit === BillItemUnit.HOUR ? LineItemKind.HOURLY : LineItemKind.FLAT_RATE,
    [BillItemTypeCategory.SUPPLIES]: LineItemKind.SUPPLIES,
    [BillItemTypeCategory.DISCOUNTS]: LineItemKind.DISCOUNT,
  };
  return translationMap[category];
};

const getBillModifierTypesFromBillItemTypes = withFragment(
  (billItemTypes) => {
    return (billItemTypes as any)
      .filter(({billStage}: any) => billStage === BillStage.POST_SUBTOTAL)
      .map(
        ({
          amount,
          description,
          identifier,
          kind,
          name,
          percentage,
          organizationId,
          nameFormulaId,
          amountFormulaId,
          id,
        }: any) => ({
          amount,
          description,
          identifier,
          kind,
          name,
          percentage,
          organizationId,
          billItemTypeId: id,
          nameFormulaId,
          amountFormulaId,
        }),
      );
  },
  gql`
    fragment BillItemType_getBillModifierTypesFromBillItemTypes on BillItemType {
      id
      amount
      billStage
      description
      identifier
      kind
      name
      percentage
      organizationId
      nameFormulaId
      amountFormulaId
    }
  `,
);

const getTemplateLineItemsFromBillItemTypes = withFragment(
  (billItemTypes) => {
    return (billItemTypes as any)
      .filter(({billStage}: any) => billStage === BillStage.PRE_SUBTOTAL)
      .map(
        ({
          id,
          category,
          description,
          identifier,
          name,
          amount,
          minQuantity,
          maxQuantity,
          unit,
          nameFormulaId,
          amountFormulaId,
          minQuantityFormulaId,
          maxQuantityFormulaId,
        }: any) => ({
          category: getLineItemCategoryFromBillItemTypeCategory(category),
          description,
          identifier,
          kind: getLineItemKindFromBillItemType({category, unit}),
          name,
          price: amount,
          minQuantity,
          maxQuantity,
          unit,
          billItemTypeId: id,
          nameFormulaId,
          priceFormulaId: amountFormulaId,
          minQuantityFormulaId,
          maxQuantityFormulaId,
        }),
      );
  },
  gql`
    fragment BillItemType_getTemplateLineItemsFromBillItemTypes on BillItemType {
      id
      amountFormulaId
      maxQuantityFormulaId
      minQuantityFormulaId
      nameFormulaId
      amount
      billStage
      category
      description
      identifier
      maxQuantity
      minQuantity
      name
      unit
    }
  `,
);

const getDisplayUpdatedAt = withFragment(
  (billItemType) => {
    return Datetime.convertToDisplayDatetime((billItemType as any).updatedAt);
  },
  gql`
    fragment BillItemType_getDisplayUpdatedAt on BillItemType {
      id
      updatedAt
    }
  `,
);

const BillItemType = {
  getDisplayBillStage,
  getDisplayValue,
  getDisplayQuantity,
  getDisplayUpdatedAt,
  getDisplayName,
  getIsSynced,
  hasAmountFormula,
  hasNameFormula,
  hasQuantityFormula,
  getBillModifierTypesFromBillItemTypes,
  getTemplateLineItemsFromBillItemTypes,
  renderNameFormulaString,
  renderAmountFormulaString,
};

export default BillItemType;
